import React from 'react';
import './PasswordStrengthMeter.css';

/**
 * Password Strength Meter Component
 * Displays real-time password strength and requirements
 */
function PasswordStrengthMeter({ password, username = '', email = '' }) {
  const [validation, setValidation] = React.useState({
    isValid: false,
    errors: [],
    strength: 'weak',
    complexity: {
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumbers: false,
      hasSpecialChars: false,
      count: 0
    }
  });

  React.useEffect(() => {
    if (!password) {
      setValidation({
        isValid: false,
        errors: [],
        strength: 'weak',
        complexity: {
          hasUpperCase: false,
          hasLowerCase: false,
          hasNumbers: false,
          hasSpecialChars: false,
          count: 0
        }
      });
      return;
    }

    // Client-side validation (mirrors backend logic)
    const errors = [];
    
    // Length check
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (password.length > 64) {
      errors.push('Password must be no more than 64 characters long');
    }
    
    // Complexity check
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    const complexityCount = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChars]
      .filter(Boolean).length;
    
    if (password.length >= 8 && complexityCount < 3) {
      errors.push('Password must contain at least 3 of the following: uppercase letters, lowercase letters, numbers, special characters');
    }
    
    // Check if password contains username/email
    const lowerPassword = password.toLowerCase();
    const lowerUsername = username ? username.toLowerCase() : '';
    const lowerEmail = email ? email.toLowerCase() : '';
    const emailUsername = lowerEmail.split('@')[0];
    
    if (lowerUsername && lowerPassword.includes(lowerUsername)) {
      errors.push('Password cannot contain your username');
    }
    if (emailUsername && lowerPassword.includes(emailUsername)) {
      errors.push('Password cannot contain your email address');
    }
    if (lowerEmail && lowerPassword.includes(lowerEmail)) {
      errors.push('Password cannot contain your email address');
    }
    
    // Check for sequential patterns
    let hasSequential = false;
    for (let i = 0; i < lowerPassword.length - 2; i++) {
      const char1 = lowerPassword.charCodeAt(i);
      const char2 = lowerPassword.charCodeAt(i + 1);
      const char3 = lowerPassword.charCodeAt(i + 2);
      
      if ((char2 === char1 + 1 && char3 === char2 + 1 && char1 >= 97 && char1 <= 122) ||
          (char2 === char1 + 1 && char3 === char2 + 1 && char1 >= 48 && char1 <= 57)) {
        hasSequential = true;
        break;
      }
    }
    if (hasSequential) {
      errors.push('Password cannot contain sequential patterns (e.g., abc, 123)');
    }
    
    // Check for repeated patterns
    if (/(.)\1{3,}/.test(password)) {
      errors.push('Password cannot contain repeated characters (e.g., aaaa, 1111)');
    }
    
    // Calculate strength
    let strength = 'weak';
    if (errors.length === 0 && password.length >= 8) {
      if (password.length >= 12 && complexityCount === 4) {
        strength = 'very-strong';
      } else if (password.length >= 10 && complexityCount >= 3) {
        strength = 'strong';
      } else if (password.length >= 8 && complexityCount >= 3) {
        strength = 'medium';
      }
    }
    
    setValidation({
      isValid: errors.length === 0 && password.length >= 8,
      errors,
      strength,
      complexity: {
        hasUpperCase,
        hasLowerCase,
        hasNumbers,
        hasSpecialChars,
        count: complexityCount
      }
    });
  }, [password, username, email]);

  const getStrengthColor = () => {
    switch (validation.strength) {
      case 'very-strong':
        return '#00c853';
      case 'strong':
        return '#64dd17';
      case 'medium':
        return '#ffd600';
      default:
        return '#ff5252';
    }
  };

  const getStrengthLabel = () => {
    switch (validation.strength) {
      case 'very-strong':
        return 'Very Strong';
      case 'strong':
        return 'Strong';
      case 'medium':
        return 'Medium';
      default:
        return 'Weak';
    }
  };

  if (!password) {
    return null;
  }

  return (
    <div className="password-strength-meter">
      <div className="password-strength-bar">
        <div 
          className="password-strength-fill"
          style={{ 
            width: `${(password.length / 64) * 100}%`,
            backgroundColor: getStrengthColor(),
            maxWidth: '100%'
          }}
        />
      </div>
      <div className="password-strength-info">
        <span className={`strength-label strength-${validation.strength}`}>
          {getStrengthLabel()}
        </span>
        <span className="password-length">
          {password.length} / 64 characters
        </span>
      </div>
      
      <div className="password-requirements">
        <h4>Password Requirements:</h4>
        <ul>
          <li className={password.length >= 8 && password.length <= 64 ? 'met' : 'unmet'}>
            Length: 8-64 characters {password.length >= 8 && password.length <= 64 ? '✓' : '✗'}
          </li>
          <li className={validation.complexity.count >= 3 ? 'met' : 'unmet'}>
            At least 3 of 4 complexity types {validation.complexity.count >= 3 ? '✓' : '✗'}
          </li>
          <li className={validation.complexity.hasUpperCase ? 'met' : 'unmet'}>
            Uppercase letters (A-Z) {validation.complexity.hasUpperCase ? '✓' : '✗'}
          </li>
          <li className={validation.complexity.hasLowerCase ? 'met' : 'unmet'}>
            Lowercase letters (a-z) {validation.complexity.hasLowerCase ? '✓' : '✗'}
          </li>
          <li className={validation.complexity.hasNumbers ? 'met' : 'unmet'}>
            Numbers (0-9) {validation.complexity.hasNumbers ? '✓' : '✗'}
          </li>
          <li className={validation.complexity.hasSpecialChars ? 'met' : 'unmet'}>
            Special characters (!@#$%^&*...) {validation.complexity.hasSpecialChars ? '✓' : '✗'}
          </li>
        </ul>
      </div>
      
      {validation.errors.length > 0 && (
        <div className="password-errors">
          <ul>
            {validation.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default PasswordStrengthMeter;
